import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Booking.css';

const Booking = () => {
  const { hotelId, roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { hotel, room, searchState } = location.state || {};

  const checkIn = searchState?.checkIn ? new Date(searchState.checkIn) : new Date();
  const checkOut = searchState?.checkOut ? new Date(searchState.checkOut) : new Date(Date.now() + 172800000);
  const nights = Math.max(1, differenceInDays(checkOut, checkIn));
  const totalPrice = room ? room.price * nights : 0;

  const [form, setForm] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    specialRequests: ''
  });

  const [roomNumber] = useState(() => {
    if (room?.roomNumbers?.length > 0) {
      return room.roomNumbers[0].number;
    }
    return 101;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  if (!hotel || !room) {
    return (
      <div>
        <Navbar />
        <div className="page-loader" style={{ marginTop: 80 }}>
          <p>No booking details found.</p>
          <button className="btn btn-dark" onClick={() => navigate('/hotels')}>Browse Hotels</button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post('/api/bookings', {
        hotel: hotelId,
        room: roomId,
        roomNumber,
        checkIn,
        checkOut,
        guests: {
          adults: searchState?.adults || 2,
          children: searchState?.children || 0
        },
        totalPrice,
        specialRequests: form.specialRequests
      });
      setSuccess(res.data.booking);
    } catch (err) {
      setError(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div>
        <Navbar />
        <div className="booking-success container">
          <div className="success-card">
            <div className="success-icon"><i class="fa-regular fa-heart-circle-check"></i></div>
            <h2>Booking Confirmed!</h2>
            <p className="success-sub">Your reservation has been successfully made. Get ready for an amazing stay!</p>
            <div className="confirmation-code">
              Confirmation Code: <strong>{success.confirmationCode}</strong>
            </div>
            <div className="success-details">
              <div className="success-row">
                <span>Property</span>
                <span>{hotel.name}</span>
              </div>
              <div className="success-row">
                <span>Room</span>
                <span>{room.title}</span>
              </div>
              <div className="success-row">
                <span>Check-in</span>
                <span>{format(checkIn, 'EEE, MMM d, yyyy')}</span>
              </div>
              <div className="success-row">
                <span>Check-out</span>
                <span>{format(checkOut, 'EEE, MMM d, yyyy')}</span>
              </div>
              <div className="success-row">
                <span>Nights</span>
                <span>{nights}</span>
              </div>
              <div className="success-row success-total">
                <span>Total Paid</span>
                <span>${totalPrice.toLocaleString()}</span>
              </div>
            </div>
            <div className="success-actions">
              <button className="btn btn-dark btn-lg" onClick={() => navigate('/my-bookings')}>
                View My Bookings
              </button>
              <button className="btn btn-outline btn-lg" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="booking-page">
      <Navbar />
      <div className="booking-container container">
        <div className="booking-breadcrumb">
          <span onClick={() => navigate('/')} className="bc-link">Home</span>
          <span className="bc-sep">›</span>
          <span onClick={() => navigate(`/hotels/${hotelId}`)} className="bc-link">{hotel.name}</span>
          <span className="bc-sep">›</span>
          <span className="bc-current">Complete Booking</span>
        </div>

        <h1 className="booking-title">Complete Your Reservation</h1>

        <div className="booking-layout">
          {/* Form */}
          <div className="booking-form-section">
            {/* Guest Details */}
            <div className="booking-card">
              <h3 className="booking-card-title">
                <span><i class="fa-regular fa-user"></i></span> Guest Information
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-input"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-input"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Special Requests (optional)</label>
                  <textarea
                    name="specialRequests"
                    className="form-input"
                    rows={3}
                    placeholder="e.g. Late check-in, high floor preference, anniversary celebration..."
                    value={form.specialRequests}
                    onChange={handleChange}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {error && <div className="alert alert-error">{error}</div>}

                {/* Payment Note */}
                <div className="payment-note">
                  <span className="payment-note-icon">🔒</span>
                  <div>
                    <p className="payment-note-title">Secure Booking</p>
                    <p className="payment-note-sub">This is a demo booking system. No real payment is processed.</p>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg confirm-btn"
                  disabled={loading}
                >
                  {loading ? 'Confirming...' : `Confirm Booking — $${totalPrice.toLocaleString()}`}
                </button>
              </form>
            </div>
          </div>

          {/* Summary */}
          <div className="booking-summary">
            <div className="summary-card">
              <img
                src={hotel.photos?.[0] || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=500'}
                alt={hotel.name}
                className="summary-img"
              />
              <div className="summary-body">
                <h3 className="summary-hotel">{hotel.name}</h3>
                <p className="summary-location">📍 {hotel.city}, {hotel.country}</p>
                <div className="summary-divider" />
                <div className="summary-room">
                  <span className="badge badge-accent">{room.title}</span>
                  <span className="summary-room-people">Max {room.maxPeople} guests</span>
                </div>
                <div className="summary-divider" />
                <div className="summary-dates">
                  <div className="summary-date">
                    <label>Check-in</label>
                    <p>{format(checkIn, 'EEE, MMM d')}</p>
                  </div>
                  <div className="summary-nights">{nights} {nights === 1 ? 'night' : 'nights'}</div>
                  <div className="summary-date">
                    <label>Check-out</label>
                    <p>{format(checkOut, 'EEE, MMM d')}</p>
                  </div>
                </div>
                <div className="summary-divider" />
                <div className="price-breakdown">
                  <div className="price-row">
                    <span>${room.price} × {nights} night{nights !== 1 ? 's' : ''}</span>
                    <span>${totalPrice.toLocaleString()}</span>
                  </div>
                  <div className="price-row price-row-fees">
                    <span>Taxes & fees</span>
                    <span>Included</span>
                  </div>
                  <div className="price-row price-total">
                    <span>Total</span>
                    <span>${totalPrice.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="summary-perks">
              {['Free cancellation available', 'No payment needed today', 'Instant confirmation'].map(p => (
                <div key={p} className="summary-perk">✓ {p}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Booking;
